<script setup lang="ts">
import {
    IonList,
    IonLabel,
    IonIcon,
    IonPage,
    IonHeader,
    IonSpinner,
    IonNote,
    IonToolbar,
    IonTitle,
    IonContent,
    IonButtons,
    IonButton,
    IonMenuButton,
    IonItem,
    IonBackButton,
    IonFabButton,
    IonItemGroup,
    IonItemDivider,
    modalController,
} from "@ionic/vue";
import ZoneForm from "@/components/modals/ZoneForm.vue";
import { ref, onMounted, computed } from "vue";
import { add } from "ionicons/icons";
import { useCostumersStore } from "@/stores/costumers"; // Adjust the import path as necessary
import MapBox from "@/components/MapBox.vue"; // Adjust the import path as necessary
import { getColoredMarkerSvg, svgToDataUri } from "@/utility";
import { IExtendedMarkerOptions } from "@/types/maps";
import { IZone } from "@/types/zone";
import { ICostumer } from "@/types/costumer";
import { se } from "date-fns/locale";

const costumerStore = useCostumersStore();
const isLoading = computed(() => costumerStore.isLoadingZones);
const zones = computed(() => costumerStore.getZones);
const markers = ref<IExtendedMarkerOptions[]>([]);
const mapReady = ref(false);

const loadZoneMarkers = () => {
    const costumersByZone: (IZone & { costumers: ICostumer[] })[] = [];
    zones.value.forEach((zone) => {
        const costumersInZone = costumerStore.getCostumers.filter((customer) => customer.zone === zone.id);
        if (costumersInZone.length > 0) {
            costumersByZone.push({
                ...zone,
                costumers: costumersInZone,
            });
        }
    });

    costumersByZone.forEach((zone) => {
        let zone_color = zone.color || "#FFFFFF"; // Default color if not specified
        zone.costumers.forEach((customer) => {
            markers.value.push({
                position: {
                    lat: Number(customer.latitude!),
                    lng: Number(customer.longitude!),
                },
                icon: svgToDataUri(getColoredMarkerSvg(zone.color)),
                title: customer.business_name,
                clickable: true,
                infoWindowHTML: `<p>Ultima ispezione: ${customer.last_inspection_delta} giorni fa</p>`,
            } as IExtendedMarkerOptions);
        });
    });
};

const openZoneForm = async () => {
    const modal = await modalController.create({
        component: ZoneForm,
    });
    modal.present();

    const result = await modal.onWillDismiss();
    if (result.role === "confirm") {
        markers.value = [];
        loadZoneMarkers();
    }
};

onMounted(() => {
    costumerStore.loadZones();
    loadZoneMarkers();
    // Delay map rendering to ensure Google Maps API is loaded
    setTimeout(() => {
        mapReady.value = true;
    }, 100);
});
</script>
<template>
    <ion-page>
        <ion-header>
            <ion-toolbar>
                <ion-buttons slot="start">
                    <ion-back-button default-href="/costumers"></ion-back-button>
                </ion-buttons>
                <ion-title>Aree</ion-title>
                <ion-buttons slot="end">
                    <ion-button fill="clear" @click="openZoneForm">
                        <ion-icon :icon="add"></ion-icon>
                        <ion-label>Nuova Area</ion-label>
                    </ion-button>
                </ion-buttons>
            </ion-toolbar>
        </ion-header>
        <ion-content style="position: relative" :fullscreen="true">
            <div class="ion-padding-horizontal ion-padding-top">
                <ion-note color="medium">
                    <p style="margin: 0">
                        Gestisci le aree geografiche per organizzare i tuoi clienti. La suddivisione in aree ti aiuta a
                        pianificare interventi e ottimizzare i percorsi.
                    </p>
                    <small>(vedi sezione pianificazione)</small>
                </ion-note>
            </div>
            <div class="ion-margin-top">
                <div v-if="!isLoading && mapReady" class="ion-padding-horizontal">
                    <!-- @vue-ignore -->
                    <map-box ref="mapBox" :max-zoom="18" :markers-data="markers" :disable-interactions="true"></map-box>
                </div>
                <ion-list v-if="!isLoading" class="ion-margin-top">
                    <ion-item
                        v-for="zone in zones"
                        :key="zone.id"
                        @click="$router.push({ name: 'zoneDetails', params: { zoneId: zone.id } })">
                        <div slot="start" class="ion-padding-top ion-padding-bottom">
                            <div
                                style="
                                    width: 30px;
                                    height: 30px;
                                    display: flex;
                                    justify-content: center;
                                    align-items: center;
                                    border-radius: 50%;
                                    border: 2px solid var(--ion-color-step-600);
                                "
                                :style="{ 'background-color': zone.color }"></div>
                        </div>
                        <ion-label>
                            {{ zone.name }}&nbsp;&nbsp;
                            <ion-note>({{ zone.costumers_count ?? 0 }} Clienti)</ion-note>
                        </ion-label>
                    </ion-item>
                </ion-list>
                <ion-item v-if="!zones.length && !isLoading" lines="none" style="text-align: center">
                    <ion-label color="medium">
                        <ion-note>
                            <h3>Nessun area trovata</h3>
                            <p>Puoi crearne una con il bottone sotto</p>
                        </ion-note>
                    </ion-label>
                </ion-item>
                <div v-if="isLoading" style="display: flex; justify-content: center; align-items: center; height: 80vh">
                    <ion-spinner></ion-spinner>
                </div>
            </div>
        </ion-content>
    </ion-page>
</template>
